import React, { useCallback, useEffect, useRef } from "react";

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

const Toolbar: React.FC<ToolbarProps> = ({ children, ...rest }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        const buttons = el.querySelectorAll<HTMLButtonElement>("button");
        const focused = Array.from(buttons).find(b => b === document.activeElement);
        const firstEnabled = Array.from(buttons).find(b => !b.disabled);
        buttons.forEach(btn => {
            btn.tabIndex = focused
                ? (btn === focused ? 0 : -1)
                : (btn === firstEnabled ? 0 : -1);
        });
    });

    const onKeyDown = useCallback((e: React.KeyboardEvent) => {
        const el = ref.current;
        if (!el) {
            return;
        }
        const buttons = Array.from(
            el.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")
        );
        const idx = buttons.indexOf(e.target as HTMLButtonElement);
        if (idx === -1) {
            return;
        }

        let next: number;
        switch (e.key) {
            case "ArrowRight":
                next = (idx + 1) % buttons.length;
                break;
            case "ArrowLeft":
                next = (idx - 1 + buttons.length) % buttons.length;
                break;
            case "Home":
                next = 0;
                break;
            case "End":
                next = buttons.length - 1;
                break;
            default:
                return;
        }
        e.preventDefault();
        buttons[idx].tabIndex = -1;
        buttons[next].tabIndex = 0;
        buttons[next].focus();
    }, []);

    return (
        <div role="toolbar"
             ref={ref}
             onKeyDown={onKeyDown}
             {...rest}
        >
            {children}
        </div>
    );
};

export default Toolbar;
